import { useLingui } from '@lingui/react/macro';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Avatar, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';
import { useAccount } from 'wagmi';
import { getBlockExplorerAddressUrl } from '@/common/blockExplorer';
import useAppStore from '../../../store/rootStore';
import { DelegationChips } from '../delegation_chips.tsx';
import { PixelAvatar } from './pixel_avatar.tsx';

interface AccountMenuHeaderProps {
  /** Resolved by RainbowKit on mainnet; undefined when the wallet has no ENS name. */
  ensName?: string | null;
  ensAvatar?: string | null;
}

function truncateAddress(value: string) {
  return `${value.substring(0, 6)}…${value.substring(value.length - 4)}`;
}

export function AccountMenuHeader({ ensName, ensAvatar }: AccountMenuHeaderProps) {
  const { t } = useLingui();
  const { address } = useAccount();
  const [, copy] = useCopyToClipboard();
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isDelegated = useAppStore((state) => state.isDelegated);
  const isDelegatedOwner = useAppStore((state) => state.isDelegatedOwner);
  const activeDelegatedAddress = useAppStore((state) => state.activeDelegatedAddress);

  const truncatedAddress = address ? truncateAddress(address) : '';
  const walletLabel = ensName || truncatedAddress;

  const copyLabel = t({ comment: 'Copy wallet address', message: 'Copy address' });
  const explorerLabel = t({ comment: 'View wallet on block explorer', message: 'View on explorer' });
  const copiedLabel = t({ comment: 'Address copied confirmation', message: 'Copied' });
  const delegatedAddressLabel = t({ comment: 'Delegated wallet address label in account menu', message: 'Delegated' });

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleCopy = useCallback(
    (value: string) => {
      copy(value);
      setCopiedAddress(value);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setCopiedAddress(null);
        timeoutRef.current = null;
      }, 2000);
    },
    [copy]
  );

  if (!address) return null;

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {ensAvatar ? (
          <Avatar src={ensAvatar} alt={walletLabel} sx={{ width: 36, height: 36, borderRadius: '20%' }} />
        ) : (
          <PixelAvatar address={address} size={36} />
        )}
        <Stack sx={{ minWidth: 0, flex: 1 }} spacing={0}>
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.25 }} className="numeric" title={address}>
            {walletLabel}
          </Typography>
          {ensName ? (
            <Typography noWrap sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.3 }} className="numeric">
              {truncatedAddress}
            </Typography>
          ) : null}
        </Stack>
        <Tooltip title={copiedAddress === address ? copiedLabel : copyLabel}>
          <IconButton
            size="small"
            onClick={() => handleCopy(address)}
            aria-label={copyLabel}
            sx={{ color: 'text.secondary', p: 0.5, '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={explorerLabel}>
          <IconButton
            size="small"
            component="a"
            href={getBlockExplorerAddressUrl(address)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={explorerLabel}
            sx={{ color: 'text.secondary', p: 0.5, '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {isDelegated || isDelegatedOwner ? (
        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
          <DelegationChips sx={{ height: 20, fontSize: 10 }} />
        </Stack>
      ) : null}

      {isDelegated && activeDelegatedAddress ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{delegatedAddressLabel}</Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }} className="numeric">
              {truncateAddress(activeDelegatedAddress)}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleCopy(activeDelegatedAddress)}
              aria-label={copyLabel}
              sx={{ color: 'text.secondary', p: 0.25 }}
            >
              <ContentCopyIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}
